import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { Send } from "lucide-react";

export default function InterviewRequestForm() {
  const [form, setForm] = useState({
    name: "", email: "", organization: "", subject_person: "",
    interview_type: "in-person", proposed_date: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const recaptcha_token = await getRecaptchaToken("interview_request");
      await api.post("/interviews/request", { ...form, recaptcha_token });
      setDone(true);
      toast.success("Request received. Julia will get back to you personally.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="border-l-4 pl-6 py-6" style={{ borderColor: "var(--accent)" }} data-testid="interview-request-done">
        <div className="kicker text-[color:var(--accent)] mb-2">Request received</div>
        <h3 className="font-serif text-3xl font-bold">Thank you, {form.name || "friend"}.</h3>
        <p className="mt-3 text-[color:var(--text-muted)]">Julia reads every request personally. You&apos;ll hear back within 48 hours.</p>
      </div>
    );
  }

  return (
    <div className="bg-[color:var(--surface)] border p-8" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="interview-request-form">
      <div className="kicker text-[color:var(--accent)] mb-2">Press office · Interview request</div>
      <h3 className="font-serif text-3xl md:text-4xl font-bold leading-tight mb-2">Request an interview with Julia</h3>
      <p className="text-[color:var(--text-muted)] mb-6">For press offices representing heads of state, sports federations, celebrities and cultural figures. Julia responds to every request personally within 48 hours.</p>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <span className="kicker block mb-1">Your name *</span>
            <input required value={form.name} onChange={(e)=>set("name",e.target.value)} className="w-full bg-transparent border px-3 py-2 outline-none" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="ir-name" />
          </label>
          <label className="block">
            <span className="kicker block mb-1">Email *</span>
            <input required type="email" value={form.email} onChange={(e)=>set("email",e.target.value)} className="w-full bg-transparent border px-3 py-2 outline-none" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="ir-email" />
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <span className="kicker block mb-1">Organization / Press office</span>
            <input value={form.organization} onChange={(e)=>set("organization",e.target.value)} className="w-full bg-transparent border px-3 py-2 outline-none" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="ir-org" />
          </label>
          <label className="block">
            <span className="kicker block mb-1">Interview subject *</span>
            <input required value={form.subject_person} onChange={(e)=>set("subject_person",e.target.value)} placeholder="e.g. President X, First Lady Y" className="w-full bg-transparent border px-3 py-2 outline-none" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="ir-subject" />
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <span className="kicker block mb-1">Format</span>
            <select value={form.interview_type} onChange={(e)=>set("interview_type",e.target.value)} className="w-full bg-transparent border px-3 py-2 outline-none" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="ir-type">
              <option value="in-person">In-person</option>
              <option value="video">Video call</option>
              <option value="phone">Phone</option>
              <option value="written">Written / email</option>
            </select>
          </label>
          <label className="block">
            <span className="kicker block mb-1">Proposed date</span>
            <input type="date" value={form.proposed_date} onChange={(e)=>set("proposed_date",e.target.value)} className="w-full bg-transparent border px-3 py-2 outline-none" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="ir-date" />
          </label>
        </div>
        <label className="block">
          <span className="kicker block mb-1">Message *</span>
          <textarea required rows={4} value={form.message} onChange={(e)=>set("message",e.target.value)} placeholder="Brief context — the news hook, availability, any embargoes." className="w-full bg-transparent border px-3 py-2 outline-none" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="ir-message" />
        </label>
        <button disabled={busy} className="btn-accent inline-flex items-center gap-2" data-testid="ir-submit">
          <Send size={14} /> {busy ? "Sending…" : "Submit request"}
        </button>
      </form>
    </div>
  );
}
